import moment from "moment";
import { LEAVE_STATUS } from "./consts";
import { IMAGE_URL } from "./urls";
import { PiSunHorizonBold } from "react-icons/pi";
import { MdOutlineWbSunny } from "react-icons/md";
import { IoMoonOutline } from "react-icons/io5";
export const extractErrorMessage = (err) => {
  if (typeof err.message === "object")
    return err.message.length > 0 ? err.message[0] : "Something went wrong !";
  else if (typeof err === "string") return err;
  else if (typeof err === "object")
    return err.message ?? "Something went wrong !";
  return err?.error ?? "Please try again!";
};

export const getAnimationConfig = (
  animateFrom,
  place = { scale: 1, x: 0, y: -180 }
) => {
  const commonConfig = place;

  switch (animateFrom) {
    case "top":
      return {
        mount: commonConfig,
        unmount: { ...commonConfig, scale: 0, y: -700 },
      };
    case "bottom":
      return {
        mount: commonConfig,
        unmount: { ...commonConfig, scale: 0, y: 700 },
      };
    case "left":
      return {
        mount: commonConfig,
        unmount: { ...commonConfig, scale: 0, x: -900, y: 0 },
      };
    case "center":
      return {
        mount: commonConfig,
        unmount: { ...commonConfig, scale: 0, x: 0, y: 0 },
      };
    default:
      return {
        mount: commonConfig,
        unmount: { ...commonConfig, scale: 0, x: 900 },
      };
  }
};

export function copyToClipboard(text) {
  try {
    // Create a textarea element
    const textarea = document.createElement("textarea");

    // Set the text content to the provided text
    textarea.value = text;

    // Make the textarea out of the viewport by setting the position to absolute
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";

    // Append the textarea to the document
    document.body.appendChild(textarea);

    // Select and copy the text inside the textarea
    textarea.select();
    document.execCommand("copy");

    // Remove the textarea from the DOM
    document.body.removeChild(textarea);

    // Return true to indicate successful copy
    return true;
  } catch (err) {
    // Return false if an error occurs (e.g., not supported or permission denied)
    return false;
  }
}

export function daysRemainingUntilNextBirthday(dobISOString) {
  const dob = new Date(dobISOString);

  if (!(dob instanceof Date) || isNaN(dob.getTime())) {
    throw new Error("Invalid date of birth");
  }

  const today = new Date();
  const nextBirthday = new Date(
    today.getFullYear(),
    dob.getMonth(),
    dob.getDate()
  );

  // If the next birthday has already occurred this year, set it to next year
  if (today > nextBirthday) {
    nextBirthday.setFullYear(today.getFullYear() + 1);
  }

  // Calculate the difference in days
  const daysRemaining = Math.ceil(
    (nextBirthday - today) / (24 * 60 * 60 * 1000)
  );

  return daysRemaining;
}

export function calculateAge(birthDate) {
  const today = new Date();
  const birth = new Date(birthDate);

  if (!(birth instanceof Date) || isNaN(birth.getTime())) {
    throw new Error("Invalid date of birth");
  }

  let age = today.getFullYear() - birth.getFullYear();

  // Adjust age if birthday hasn't occurred yet this year
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age--;
  }

  return age;
}

export function getImageUrl(name) {
  return `${IMAGE_URL}${name}`;
}

export function shouldIDoThis() {
  return Math.random() < 0.5;
}

export function showSafeText(text, maxLength) {
  if (text.length <= maxLength) {
    return text;
  } else {
    return text.substring(0, maxLength) + "...";
  }
}

export const getLeaveColorByStatus = (status) => {
  switch (status) {
    case LEAVE_STATUS.PENDING:
      return "bg-blue-900 text-white";
    case LEAVE_STATUS.ACCEPTED:
      return "bg-green-900 text-white";
    case LEAVE_STATUS.REJECTED:
      return "bg-red-900 text-white";
  }
};

export const getLeaveColor = (status1, status2, status3) => {
  // Convert status strings to uppercase to ensure consistency
  const statuses = [
    status1.toUpperCase(),
    status2.toUpperCase(),
    status3.toUpperCase(),
  ];

  if (statuses.includes(LEAVE_STATUS.REJECTED)) return "bg-red-900 text-white";

  // Check if all statuses are the same
  const allSame = statuses.every(
    (status) =>
      status === LEAVE_STATUS.PENDING ||
      status === LEAVE_STATUS.ACCEPTED ||
      status === LEAVE_STATUS.REJECTED
  );

  if (allSame) {
    // All statuses are the same
    if (statuses[0] === LEAVE_STATUS.PENDING) {
      // All pending
      return "bg-orange-300";
    } else if (statuses[0] === LEAVE_STATUS.REJECTED) {
      // All rejected
      return "bg-red-300";
    } else if (statuses[0] === LEAVE_STATUS.ACCEPTED) {
      // All accepted
      return "bg-green-300";
    }
  } else {
    // Statuses are different
    return "bg-gray-300";
  }
};

export function isTodayOrAfterLeaveStartDate(leaveStartDate) {
  if (!leaveStartDate) {
    return false; // Handle the case when leaveStartDate is undefined or null
  }

  // Get today's date as a Moment.js object, set to the start of the day
  const today = moment().startOf("day");

  // Parse the leave start date as a Moment.js object
  const leaveDateMoment = moment(leaveStartDate).isSameOrAfter(moment(), "day");

  // Check if today's date is the same or after the leave start date
  return leaveDateMoment;
}

export function getGreetingBasedOnTime() {
  const currentHour = new Date().getHours();

  if (currentHour >= 5 && currentHour < 12) {
    return {
      name: "Good Morning",
      Icon: MdOutlineWbSunny,
    };
  } else if (currentHour >= 12 && currentHour < 18) {
    return {
      name: "Good Afternoon",

      Icon: PiSunHorizonBold,
    };
  } else if (currentHour >= 17 && currentHour < 21) {
    return {
      name: "Good Evening",
      Icon: PiSunHorizonBold,
    };
  } else {
    return {
      name: "Good Night",
      Icon: IoMoonOutline,
    };
  }
}

export function maskString(input) {
  if (typeof input !== "string" || input.length <= 2) {
    return input; // If input is not a string or its length is 2 or less, return it as is
  }

  let maskedSection = "*".repeat(input.length - 2);
  let visibleSection = input.slice(-2);

  return maskedSection + visibleSection;
}

export const getImage = (image) => {
  return typeof image === "string" || !(image instanceof Blob)
    ? `${IMAGE_URL}${image}`
    : URL.createObjectURL(image);
};










// helpers for application
export const formatAddress = (addressObj) => {
  const addressParts = [
    addressObj.registerAddressSubBuildingName,
    addressObj.registerAddressBuildingName,
    addressObj.registerAddressBuildingNumber,
    addressObj.registerAddressStreet,
    addressObj.registerAddressTownOrCity,
    addressObj.registerAddressPostCode,
    addressObj.registerAddressState,
    addressObj.registerAddressCountry,
  ];

  // Filter out empty strings and join the remaining parts with a comma
  return addressParts.filter(part => part).join(', ');
};



export function capitalizeName(name) {
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}




export function addThousandSeparator(num) {
  if (num === undefined || num === null) return '';

  let [integerPart, decimalPart] = num.toString().split('.');

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return decimalPart ? `${integerPart}.${decimalPart}` : `${integerPart}.00`;
}

